import { parseDate, today } from "@internationalized/date";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { orderBy } from "lodash-es";
import { useMemo } from "react";

import { isAdminRole, useViewer, useViewerHasPermission } from "@/auth/shared";
import { getPoolStats, type PoolTeamStats } from "@/hooks/matches";
import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";
import { isDefined } from "@/utils/types";

export function useTournament() {
  const trpc = useTRPC();
  const { tournamentId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const { data: tournament } = useSuspenseQuery(
    trpc.tournaments.get.queryOptions({
      id: Number.parseInt(tournamentId, 10),
    }),
  );

  return tournament;
}

export function useTournamentDivision() {
  const { divisionId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const tournament = useTournament();

  return (
    tournament?.tournamentDivisions.find(
      ({ id }) => id.toString() === divisionId,
    ) ?? tournament?.tournamentDivisions[0]
  );
}

export function useIsTournamentToday() {
  const tournament = useTournament();

  if (!tournament) {
    return;
  }

  return parseDate(tournament.date).compare(today(getDefaultTimeZone())) <= 0;
}

export function useIsDemoTournament() {
  const tournament = useTournament();

  return tournament?.demo || import.meta.env.VITE_DEMO_MODE === "true";
}

export function useCanUpdateTournament(): boolean {
  const viewer = useViewer();
  const tournament = useTournament();
  const hasPermission = useViewerHasPermission({ tournaments: ["update"] });

  const trpc = useTRPC();
  const { tournamentId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const { data: directors } = useQuery({
    ...trpc.tournaments.getDirectors.queryOptions({
      tournamentId: Number.parseInt(tournamentId, 10),
    }),
    enabled: !!viewer && !!hasPermission && !!tournament,
  });

  if (!viewer || !hasPermission || !tournament) return false;
  if (isAdminRole(viewer.role)) return true;

  return (
    directors?.some((td) => td.director.profile.userId === viewer.id) ?? false
  );
}

export function useActiveDivisionId() {
  const { divisionId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  return Number.parseInt(divisionId, 10);
}

export function usePoolsQueryOptions() {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();

  return {
    ...trpc.tournaments.getPools.queryOptions({
      tournamentDivisionId,
    }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  };
}

export function usePoolNamesQueryOptions() {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();

  return {
    ...trpc.tournaments.getPoolNames.queryOptions({
      tournamentDivisionId,
    }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  };
}

export function usePoolMatches() {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();

  const { data } = useQuery({
    ...trpc.tournaments.getPools.queryOptions({
      tournamentDivisionId,
    }),
    select: (data) => data.flatMap((pool) => pool.matches),
  });

  return data;
}

export function useHasPendingPoolMatches() {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();

  const { data } = useQuery({
    ...trpc.tournaments.getDivisionSummary.queryOptions({
      tournamentDivisionId,
    }),
    select: (data) => data.hasPendingPoolMatches,
  });

  return data;
}

export function usePlayoffsQueryOptions() {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();

  return {
    ...trpc.tournaments.getPlayoffs.queryOptions({
      tournamentDivisionId,
    }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  };
}

export function usePlayoffMatches() {
  const query = usePlayoffsQueryOptions();

  const { data: playoffs } = useQuery({
    ...query,
  });

  return playoffs;
}

export function usePlayoffMatch(matchId: number) {
  const query = usePlayoffsQueryOptions();

  const { data } = useQuery({
    ...query,
    select: (data) => data.find(({ id }) => id === matchId),
  });

  return data;
}

export function useIsPlayoffsComplete() {
  const query = usePlayoffsQueryOptions();

  const { data } = useQuery({
    ...query,
  });

  return data && data.length > 0
    ? data.every(({ status }) => status === "completed")
    : false;
}

export function useTeamsQueryOptions() {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();

  return {
    ...trpc.tournaments.getTeams.queryOptions({
      tournamentDivisionId,
    }),
    staleTime: 60_000,
  };
}

export function useTeamMap() {
  const query = useTeamsQueryOptions();

  const { data: teams } = useQuery(query);

  const teamMap = useMemo(() => {
    if (!teams) return new Map<number, (typeof teams)[number]>();
    return new Map(teams.map((team) => [team.id, team]));
  }, [teams]);

  return { teamMap, teams };
}

export function useActiveTeams(tournamentDivisionId: number) {
  const trpc = useTRPC();
  const { data: teams } = useQuery({
    ...trpc.tournaments.getTeams.queryOptions({ tournamentDivisionId }),
    select: (data) =>
      data.filter(({ status }) => ["confirmed", "registered"].includes(status)),
  });

  return teams;
}

export function useActiveTeamsFromUrl() {
  const id = useActiveDivisionId();

  return useActiveTeams(id);
}

export function useTeam(id: number) {
  const query = useTeamsQueryOptions();

  const { data: teams } = useQuery({
    ...query,
    select: (data) => data.find((team) => id === team.id),
  });

  return teams;
}

export function useWaitlist() {
  const query = useTeamsQueryOptions();

  const { data: waitlist } = useQuery({
    ...query,
    select: (data) => data.filter(({ status }) => status === "waitlisted"),
  });

  return waitlist
    ? orderBy(waitlist, ["order", "createdAt"], ["asc", "asc"])
    : undefined;
}

export function useTeamsAtCapacity() {
  const trpc = useTRPC();
  const { divisionId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const division = useTournamentDivision();

  const { data: atCapacity } = useSuspenseQuery({
    ...trpc.tournaments.getTeams.queryOptions({
      tournamentDivisionId: Number.parseInt(divisionId, 10),
    }),
    select: (data) => data.length === division?.capacity,
  });

  return atCapacity;
}

export function usePools() {
  const trpc = useTRPC();
  const { divisionId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const { data } = useSuspenseQuery({
    ...trpc.tournaments.getPools.queryOptions({
      tournamentDivisionId: Number.parseInt(divisionId, 10),
    }),
  });

  return data;
}

export function usePoolStats(poolId: number) {
  const pools = usePools();

  const teamsQuery = useTeamsQueryOptions();

  const pool = pools?.find(({ id }) => id === poolId);

  const { data: teams } = useQuery({
    ...teamsQuery,
    select: (data) => data.filter((team) => team.poolTeam.poolId === poolId),
    enabled: isDefined(pool),
  });

  if (!pool) {
    return undefined;
  }

  if (!teams) {
    return undefined;
  }

  const stats = getPoolStats(pool);

  if (!stats) {
    return undefined;
  }

  const statsMap = new Map(
    Object.keys(stats).map(
      (teamId) =>
        [Number.parseInt(teamId, 10), stats[teamId as unknown as number]] as [
          number,
          PoolTeamStats,
        ],
    ),
  );

  const orderedTeams = orderBy(
    teams.map((team) => ({
      ...team,
      stats: statsMap?.get(team.id),
    })),
    [
      (team) => team.poolTeam?.finish,
      (team) => {
        const teamStats = statsMap.get(team.id);

        if (teamStats) {
          return (
            teamStats.wins.size / (teamStats.wins.size + teamStats.losses.size)
          );
        }

        return 0;
      },
      (team) => statsMap.get(team.id)?.totalPointDiff,
      (team) => team.poolTeam?.pool.name,
    ],
    ["asc", "desc", "desc", "asc"],
  );

  return orderedTeams;
}

export function useLastSeed() {
  const options = useTeamsQueryOptions();

  const { data: teams } = useQuery(options);

  const lastSeed = Math.max(
    ...(teams?.map((team) => team.seed).filter(isDefined) ?? []),
  );

  return lastSeed;
}
